
import Address from './Address';
import axios from 'axios';
import OrderContract from '../contracts/Order';
import Resource from './Resource';

/**
 * Order status
 * @enum
 */
enum OrderStatus
{
    created = 'created',
    paid = 'paid',
    fulfilled = 'fulfilled'
}

/**
 * Order class
 * @class
 **/
class Order extends Resource implements OrderContract
{
    /**
     * Resource identifier
     * @type {String}
     **/
    static handle = 'order_id';

    /**
     * Default attributes
     * @type {Object}
     **/
    static attributes: OrderContract = {
        order_id: undefined,
        account: undefined,
        telephone: undefined,
        billing_fullname: undefined,
        billing_line1: undefined,
        billing_line2: undefined,
        billing_city: undefined,
        billing_county: undefined,
        billing_country: undefined,
        billing_postcode: undefined,
        shipping_fullname: undefined,
        shipping_line1: undefined,
        shipping_line2: undefined,
        shipping_city: undefined,
        shipping_county: undefined,
        shipping_country: 'GB',
        shipping_postcode: undefined,
        stripe_intent_id: undefined,
        collection: false,
        status: OrderStatus.created,
    };

    /**
     * Resource attributes
     * @type {OrderContract}
     **/
    attributes: OrderContract;

    /**
     * Returns true if order should be shipped to billing address, false otherwise.
     * @return {Boolean}
     */
    ship_to_billing: boolean;

    /**
     * Current attributes
     **/
    readonly order_id: number;
    account: string;
    telephone: string;
    billing_fullname: string;
    billing_line1: string;
    billing_line2: string;
    billing_city: string;
    billing_county: string;
    billing_country: string;
    billing_postcode: string;
    shipping_fullname: string;
    shipping_line1: string;
    shipping_line2: string;
    shipping_city: string;
    shipping_county: string;
    shipping_country: string;
    shipping_postcode: string;
    stripe_intent_id: string;
    stripe_card_id: string;
    collection: boolean;
    status: string;

    /**
     * Order constructor
     * @param {Object} [attributes={}]
     * @param {Boolean} [exists=false]
     **/
    constructor( attributes: any = {}, exists: boolean = false )
    {
        super( attributes, exists );

        this.ship_to_billing = true;
    }


    get shipToBilling()
    {
        return this.ship_to_billing && this.canShipToBilling;
    }


    get canShipToBilling()
    {
        return ( this.billing_country === 'GB' || this.billing_country === null || this.billing_country === undefined );
    }



    /**
     * Get all orders
     * @return {Promise<Order[]>}
     */
    static all(): Promise<Order[]>
    {
        return axios.get( '/api/orders' )
            .then( response => response.data.map( attributes => new Order( attributes, true ) ) );
    }


    /**
     * Get order by id
     * @param {Number} id
     */
    static fetch( id: number ): Promise<Order>
    {
        return axios.get( `/api/order/${ id }` )
            .then( response => new Order( response.data, true ) );
    }


    /**
     * Get order by id
     * @param {Number} id
     */
    static details( id: number ): Promise<any>
    {
        return axios.get( `/api/order-details/${ id }` )
            .then( response => response.data );
    }


    get billing()
    {
        return {
            fullname: this.billing_fullname,
            line1: this.billing_line1,
            line2: this.billing_line2,
            city: this.billing_city,
            county: this.billing_county,
            country: this.billing_country,
            postcode: this.billing_postcode,
        };
    }


    get shipping()
    {
        if( this.shipToBilling )
            return this.billing;

        return {
            fullname: this.shipping_fullname,
            line1: this.shipping_line1,
            line2: this.shipping_line2,
            city: this.shipping_city,
            county: this.shipping_county,
            country: this.shipping_country,
            postcode: this.shipping_postcode,
        };
    }


    /**
     * Process order
     * @return {Promise}
     */
    process()
    {
        let attributes: any = { card_id: this.stripe_card_id, ...this.current };

        if( !this.collection && this.shipToBilling ) {
            attributes.ship_to_billing = true;
            attributes = {
                ...attributes,
                shipping_line1: this.billing_line1,
                shipping_line2: this.billing_line2,
                shipping_city: this.billing_city,
                shipping_county: this.billing_county,
                shipping_country: this.billing_country,
                shipping_postcode: this.billing_postcode,
            };
        }

        return axios.post( `/api/order/${ this.order_id }/process`, attributes );
    }


    /**
     * Save order
     * @return {Promise<Order>}
     */
    save(): Promise<Order>
    {
        this.saving = true;
        return axios.patch( `/api/order/${ this.order_id }`, this.changes ).then( response => {
            this.sync( response.data );
            this.saving = false;

            return this as Order;
        });
    }


    /**
     * Get resource validation rules
     * @return {Object}
     **/
    rules(): any
    {
        let rules = {
            'account': ['required'],
            'telephone': ['required'],
            'billing.fullname': ['required'],
            'billing.line1': ['required'],
            'billing.city': ['required'],
            'billing.country': ['required'],
            'billing.postcode': ['required'],
        };

        if( ! this.collection ) {
            const extended = {
                'shipping.fullname': ['required'],
                'shipping.line1': ['required'],
                'shipping.city': ['required'],
                'shipping.country': ['required'],
                'shipping.postcode': ['required'],
            };

            rules = { ...rules, ...extended };
        }

        return rules;
    }
}

Resource.register( Order );

export default Order;
